import React from 'react';
import { Modal } from '../../../components/ui/Modal';
import { CheckCircle2, AlertTriangle } from 'lucide-react';
import { DigitalMarketingDeleteModal } from 'features/DigitalMarketing/components/DigitalMarketingDeleteModal';
import { DMButton, DMDiv, DMText } from '../../../components/Primitives';

export type BlogModalState =
  | 'delete'
  | 'success_publish'
  | 'success_unpublish'
  | 'success_delete'
  | 'limit'
  | 'warning'
  | null;

export interface GeneralModalsProps {
  activeModal: BlogModalState;
  setActiveModal: (modal: BlogModalState) => void;
  isEditMode: boolean;
  isDeletingBlog: boolean;
  handleDeleteConfirm: () => void;
  warningMessage: string;
}

export const GeneralModals = ({
  activeModal,
  setActiveModal,
  isEditMode,
  isDeletingBlog,
  handleDeleteConfirm,
  warningMessage,
}: GeneralModalsProps) => {
  return (
    <>
      <Modal
        isOpen={
          activeModal === 'success_publish' ||
          activeModal === 'success_unpublish'
        }
      >
        <DMDiv className="flex flex-col items-center py-4 text-center">
          <DMDiv className="mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-[#22C55E]">
            <CheckCircle2 size={40} className="text-white" />
          </DMDiv>
          <DMText as="h2" className="mb-2 text-2xl font-bold text-gray-900">
            {activeModal === 'success_unpublish'
              ? 'Blog Unpublished'
              : isEditMode
              ? 'Blog Updated'
              : 'Blog Published'}
          </DMText>
          <DMText className="text-gray-500">
            Your blog has been{' '}
            {activeModal === 'success_unpublish'
              ? 'unpublished from'
              : isEditMode
              ? 'updated on'
              : 'published on'}{' '}
            the website.
          </DMText>
        </DMDiv>
      </Modal>

      <DigitalMarketingDeleteModal
        isOpen={activeModal === 'delete'}
        title="Are you sure you want to delete this blog?"
        isDeleting={isDeletingBlog}
        onCancel={() => setActiveModal(null)}
        onConfirm={handleDeleteConfirm}
      />

      <Modal isOpen={activeModal === 'success_delete'}>
        <DMDiv className="flex flex-col items-center py-4">
          <DMDiv className="mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-[#22C55E]">
            <CheckCircle2 size={40} className="text-white" />
          </DMDiv>
          <DMText as="h2" className="mb-2 text-2xl font-bold text-gray-900">
            Blog deleted successfully
          </DMText>
          <DMText className="text-gray-500">
            Your details are no longer visible on the school website.
          </DMText>
        </DMDiv>
      </Modal>

      <Modal
        isOpen={activeModal === 'limit'}
        onClose={() => setActiveModal(null)}
      >
        <DMDiv className="flex flex-col items-center py-4">
          <DMDiv className="mb-6 flex h-20 w-20 items-center justify-center rounded-2xl bg-[#FBCC34]">
            <AlertTriangle size={40} className="text-white" />
          </DMDiv>
          <DMText as="h2" className="mb-2 text-2xl font-bold text-gray-900">
            Limit Reached
          </DMText>
          <DMText className="text-gray-500">
            Limit has been reached. Cannot generate anymore content.
          </DMText>
        </DMDiv>
      </Modal>

      <Modal
        isOpen={activeModal === 'warning'}
        onClose={() => setActiveModal(null)}
      >
        <DMDiv className="flex flex-col items-center py-4 text-center">
          <DMDiv className="mb-6 flex h-20 w-20 items-center justify-center rounded-2xl bg-[#FBCC34]">
            <AlertTriangle size={40} className="text-white" />
          </DMDiv>
          <DMText as="h2" className="mb-2 text-2xl font-bold text-gray-900">
            Attention
          </DMText>
          <DMText className="mb-8 text-gray-500">{warningMessage}</DMText>
          <DMButton
            onClick={() => setActiveModal(null)}
            className="w-full rounded-full bg-[#5E4DE0] py-3 font-medium text-white transition-colors hover:bg-[#4638C0]"
          >
            Got it
          </DMButton>
        </DMDiv>
      </Modal>
    </>
  );
};
